import { useCallback, useState } from 'react';
import { sendMessage, getConversationMessages } from '../services/conversations.js';
import { detectContentFormat, resolveMessageSourceContent } from '../utils/contentUtils.js';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_WAIT_MS = 60000;
const MESSAGE_STATUS_POLL_MS = 1500;
/** send-asynchronous POST can exceed the API client's default timeout (10s). */
const DOCUMENT_REWRITE_SEND_TIMEOUT_MS = 180_000;

/**
 * Human-readable message text (same approach as `useChatMessages` + API snake_case fields).
 * @param {unknown} msg
 */
function getMessageContent(msg) {
  if (!msg) return '';
  const resolved = resolveMessageSourceContent(msg);
  if (resolved && String(resolved).trim()) return String(resolved).trim();
  const fallback =
    msg?.formattedText ??
    msg?.formatted_text ??
    msg?.messageText ??
    msg?.message_text ??
    msg?.message ??
    msg?.content ??
    msg?.text ??
    msg?.body ??
    '';
  return typeof fallback === 'string' ? fallback.trim() : String(fallback || '').trim();
}

/**
 * @param {unknown} msg
 */
function isAssistantMessage(msg) {
  const senderRole = String(msg?.senderRole || msg?.role || '').toUpperCase();
  const direction = String(msg?.direction || '').toUpperCase();
  return (
    senderRole === 'AGENT' ||
    msg?.role === 'assistant' ||
    direction === 'OUTBOUND'
  );
}

/**
 * User message (INBOUND / USER). If it appears **after** the latest assistant message, the new reply is not ready yet.
 * @param {unknown} msg
 */
function isUserMessage(msg) {
  const senderRole = String(msg?.senderRole || msg?.role || '').toUpperCase();
  const direction = String(msg?.direction || '').toUpperCase();
  return (
    senderRole === 'USER' ||
    msg?.role === 'user' ||
    direction === 'INBOUND'
  );
}

/**
 * @param {unknown} sendResponse
 */
function getConversationIdFromSendResponse(sendResponse) {
  if (!sendResponse || typeof sendResponse !== 'object') return null;
  const body = sendResponse.body;
  const data = sendResponse.data;
  return (
    sendResponse.conversationUuid ??
    sendResponse.conversation_uuid ??
    sendResponse.conversationId ??
    (body && typeof body === 'object'
      ? body.conversationUuid ?? body.conversation_uuid ?? body.conversationId
      : null) ??
    (data && typeof data === 'object'
      ? data.conversationUuid ?? data.conversation_uuid ?? data.conversationId
      : null)
  );
}

/**
 * @param {unknown} sendResponse
 */
function getRequestIdFromSendResponse(sendResponse) {
  if (!sendResponse || typeof sendResponse !== 'object') return null;
  const body = sendResponse.body;
  const data = sendResponse.data;
  return (
    sendResponse.request_id ??
    sendResponse.requestId ??
    (body && typeof body === 'object' ? body.request_id ?? body.requestId : null) ??
    (data && typeof data === 'object' ? data.request_id ?? data.requestId : null)
  );
}

function htmlishToPlainText(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return '';
  const fmt = detectContentFormat(raw);
  if (fmt !== 'html') return raw.trim();
  return raw
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Polls GET /message/status/:requestId until completed (legacy ann path).
 * @param {import('axios').AxiosInstance} apiClient
 * @param {string} requestId
 */
async function pollMessageStatusForContent(apiClient, requestId) {
  const startedAt = Date.now();
  while (Date.now() - startedAt < POLL_MAX_WAIT_MS) {
    try {
      const data = await apiClient.get(`/message/status/${requestId}`);
      const status = data?.status ?? data?.body?.status;
      if (status === 'completed' || status === 'done') {
        const content =
          data?.body?.messageText ??
          data?.body?.content ??
          data?.body?.message ??
          data?.content ??
          data?.messageText ??
          data?.message ??
          (typeof data?.body === 'string' ? data.body : null);
        if (content && String(content).trim()) {
          return String(content).trim();
        }
      }
      if (status === 'error' || status === 'failed') {
        throw new Error(data?.body?.message ?? data?.message ?? 'Message status error');
      }
    } catch (e) {
      if (e?.message && (String(e.message).includes('status') || String(e.message).includes('Message'))) {
        throw e;
      }
    }
    await new Promise((r) => setTimeout(r, MESSAGE_STATUS_POLL_MS));
  }
  return null;
}

/**
 * Document AI rewrite via send-asynchronous + poll (same idea as ann-web-app
 * `useDocumentRewrite` / concierge agent). Does **not** mutate the active chat thread in context:
 * send uses a dedicated `rewriteAgentUuid` and optional null conversation.
 *
 * @param {object} params
 * @param {import('axios').AxiosInstance} params.apiClient
 * @param {string} params.rewriteAgentUuid — e.g. `import.meta.env.VITE_DOCUMENT_REWRITE_AGENT_UUID` (reasoning / concierge assistant UUID).
 * @param {() => string} params.getContent — Plain-text body of the document to send to the model.
 * @param {(text: string) => void} params.onReplaceContent — Receives plain text to store as document body.
 */
export function useDocumentRewrite({ apiClient, rewriteAgentUuid, getContent, onReplaceContent } = {}) {
  const [isRewriting, setIsRewriting] = useState(false);

  const rewrite = useCallback(
    async (instruction) => {
      if (!apiClient) {
        throw new Error('Document rewrite requires apiClient');
      }
      if (!rewriteAgentUuid || !String(rewriteAgentUuid).trim()) {
        throw new Error(
          'Document rewrite agent is not configured (set VITE_DOCUMENT_REWRITE_AGENT_UUID to the concierge / reasoning assistant UUID).',
        );
      }

      const baseInstruction = typeof instruction === 'string' ? instruction.trim() : '';
      if (!baseInstruction) {
        return;
      }

      const rawContent = getContent?.() ?? '';
      const content = htmlishToPlainText(typeof rawContent === 'string' ? rawContent : String(rawContent || ''));

      if (!content.trim()) {
        return;
      }

      const fullMessage = `${baseInstruction}\n\nDocument to edit:\n${content}`;

      setIsRewriting(true);
      try {
        const sendResponse = await sendMessage(
          apiClient,
          {
            messageText: fullMessage,
            conversationUuid: '',
            agentUuid: String(rewriteAgentUuid).trim(),
            channel: 'WEB',
          },
          { timeout: DOCUMENT_REWRITE_SEND_TIMEOUT_MS },
        );

        const conversationUuid = getConversationIdFromSendResponse(sendResponse);
        const requestId = getRequestIdFromSendResponse(sendResponse);

        let finalContent = '';

        if (conversationUuid) {
          const startedAt = Date.now();
          while (Date.now() - startedAt < POLL_MAX_WAIT_MS) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

            const listResponse = await getConversationMessages(apiClient, {
              conversationId: conversationUuid,
            });
            const list = Array.isArray(listResponse?.body)
              ? listResponse.body
              : Array.isArray(listResponse?.messages)
                ? listResponse.messages
                : Array.isArray(listResponse?.data)
                  ? listResponse.data
                  : [];

            const sorted = [...list].sort(
              (a, b) =>
                new Date(
                  a.createdAt || a.lastUpdated || a.updatedAt || a.timestamp || 0,
                ).getTime() -
                new Date(
                  b.createdAt || b.lastUpdated || b.updatedAt || b.timestamp || 0,
                ).getTime(),
            );
            const assistantMsgs = sorted.filter(isAssistantMessage);
            const assistantMsg = assistantMsgs.length
              ? assistantMsgs[assistantMsgs.length - 1]
              : null;
            if (assistantMsg) {
              const idx = sorted.indexOf(assistantMsg);
              const hasUserAfterAssistant =
                idx >= 0 &&
                sorted.slice(idx + 1).some((m) => isUserMessage(m));
              if (hasUserAfterAssistant) {
                continue;
              }
              const text = getMessageContent(assistantMsg);
              if (text && String(text).trim()) {
                finalContent = String(text).trim();
                break;
              }
            }
          }
        } else if (requestId) {
          finalContent = await pollMessageStatusForContent(apiClient, requestId);
        }

        if (!finalContent) {
          if (!conversationUuid && !requestId) {
            // eslint-disable-next-line no-console
            console.warn('useDocumentRewrite :: send response shape', sendResponse);
            throw new Error(
              'Backend did not return conversation ID or request_id; cannot retrieve assistant response.',
            );
          }
          throw new Error('No response from document rewrite agent within timeout or empty content.');
        }

        onReplaceContent?.(finalContent);
      } finally {
        setIsRewriting(false);
      }
    },
    [apiClient, rewriteAgentUuid, getContent, onReplaceContent],
  );

  return {
    rewrite,
    isRewriting,
  };
}

export { useDocumentRewrite as useHomeDocumentRewrite };
